// S2-0f.2: Fetch evidence data for a completed eval run
import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

// Mirrors evidence-generator.ts types
export interface ProductScore {
  product_id: string;
  product_name: string;
  score: number;
  pass: boolean;
  reasoning: string;
  issues: string[];
  evidence: Record<string, unknown>;
}

export interface GateEvidence {
  gate_id: string;
  gate_name: string;
  score: number;
  threshold: number;
  pass: boolean;
  blocking: boolean;
  reasoning: string;
  product_scores: ProductScore[];
  weakest_sub_metric?: {
    id: string; name: string; score: number; target: number; gap: number;
    products_failing: string[];
  };
  improvement?: {
    current: number; target_if_fixed: number; fix_description: string; roi: string;
  };
  fixes: { knob_id: string; action_type: string; reason: string; priority: number }[];
}

export interface DimensionEvidence {
  dimension_id: string;
  name: string;
  score: number;
  weight: number;
  threshold: number;
  pass: boolean;
  reasoning: string;
  product_scores: ProductScore[];
  content_evidence: Record<string, unknown>;
  improvement?: { impact_on_overall: number; fix_description: string };
}

export interface CrossMetricInference {
  type: 'correlation' | 'systemic' | 'opportunity' | 'anomaly';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  explanation: string;
  affected_metrics: string[];
  affected_products: string[];
  fix?: string;
}

export interface EvidencePackage {
  run_ids: string[];
  agent_id: string;
  overall_quality: number;
  gates: GateEvidence[];
  dimensions: DimensionEvidence[];
  cross_metric: CrossMetricInference[];
  summary: {
    total_gates: number; passing: number; failing: number;
    blocking: string[]; highest_roi_fix: string; critical_products: string[];
  };
}

export function useEvidence(runId: string | null) {
  const [evidence, setEvidence] = useState<EvidencePackage | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!runId) { setEvidence(null); return; }

    setLoading(true);
    apiFetch<EvidencePackage>(`/v1/eval/runs/${runId}/evidence`)
      .then(setEvidence)
      .catch(() => setEvidence(null))
      .finally(() => setLoading(false));
  }, [runId]);

  return { evidence, loading };
}
